"use client";

import { motion } from "motion/react";
import { Sparks } from "iconoir-react";
import Link from "next/link";
import { CREATE_EVENT_FRESH_START_PARAM } from "../_logic/constants";

interface ResumeDraftBannerProps {
  /** The resumed draft's event name, shown so the user knows which draft they're back in. */
  draftName: string | null;
}

/**
 * Shown above the wizard when a signed-in user's most recent open draft was silently resumed
 * (the common case — most entry points into `/create` don't pass the explicit
 * {@link CREATE_EVENT_FRESH_START_PARAM} that skips resume). Without this, a user landing back
 * mid-wizard has no way to tell they're continuing an old, possibly abandoned draft rather than
 * starting fresh — and no in-UI way to start over if that's what they actually wanted.
 */
export const ResumeDraftBanner = ({ draftName }: ResumeDraftBannerProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="flex items-start gap-3 rounded-2xl bg-indigo-50/70 px-4 py-3 text-left ring-1 ring-inset ring-indigo-100"
      role="status"
    >
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-indigo-600">
        <Sparks className="h-4 w-4" strokeWidth={2} />
      </span>
      <div className="flex flex-1 items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-semibold text-gray-900 tracking-tight">Picking up where you left off</p>
          <p className="text-sm font-medium text-gray-700">
            {draftName ? (
              <>
                Continuing your draft <span className="font-semibold">&quot;{draftName}&quot;</span>.
              </>
            ) : (
              "Continuing your most recent draft."
            )}
          </p>
        </div>
        <Link
          href={`/create?${CREATE_EVENT_FRESH_START_PARAM}`}
          className="shrink-0 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
        >
          Start a new event
        </Link>
      </div>
    </motion.div>
  );
};
